import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { getOrderDetails } from "../../services/order.service";
import {
  createShiprocketOrder,
  checkShiprocketServiceability,
  assignShiprocketAwb,
  getShiprocketStatus,
  generateShiprocketLabel,
  scheduleShiprocketPickup,
  trackShiprocketShipment,
  generateShiprocketManifest,
} from "../../services/shiprocket.service";
import { toast } from "react-toastify";
import CustomField from "../../components/common/customField";
import {
  MdArrowBack,
  MdLocalShipping,
  MdPerson,
  MdLocationOn,
  MdPayment,
  MdReceipt,
  MdInventory,
  MdCheckCircle,
  MdPrint,
  MdSchedule,
  MdDescription,
} from "react-icons/md";

/* ─────────────────────────── Reusable Style Constants ─────────────────────────── */

const cardClass =
  "bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl p-5 shadow-sm";
const headingClass =
  "font-semibold text-gray-800 dark:text-white flex items-center gap-2 mb-4";
const labelClass = "text-gray-500 dark:text-gray-400";
const valueClass = "font-medium dark:text-gray-200";
const successBannerClass =
  "p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg";
const successTextClass = "text-sm text-green-800 dark:text-green-300";
const btnBase =
  "inline-flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const fullBtnBase =
  "w-full px-4 py-3 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const spinnerClass =
  "animate-spin rounded-full h-4 w-4 border-b-2 border-white";

/* ─────────────────────────── Helper: Status Badge Color ─────────────────────────── */

const getStatusColor = (status) => {
  const colors = {
    Delivered:
      "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
    Shipped: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
    Cancelled: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  };
  return (
    colors[status] ||
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
  );
};

/* ─────────────────────────── Helper: Format Date ─────────────────────────── */

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

/* ═══════════════════════════ Sub-Components ═══════════════════════════ */

/** Reusable action button with loading spinner */
const ActionButton = ({
  onClick,
  loading,
  loadingText,
  icon: Icon,
  label,
  color = "bg-blue-600 hover:bg-blue-700",
}) => (
  <button
    onClick={onClick}
    disabled={loading}
    className={`${btnBase} ${color}`}
  >
    {loading ? (
      <>
        <span className={spinnerClass} />
        {loadingText}
      </>
    ) : (
      <>
        {Icon && <Icon size={18} />}
        {label}
      </>
    )}
  </button>
);

/** Reusable full-width action button with loading spinner */
const FullWidthButton = ({
  onClick,
  loading,
  loadingText,
  label,
  color = "bg-blue-600 hover:bg-blue-700",
}) => (
  <button
    onClick={onClick}
    disabled={loading}
    className={`${fullBtnBase} ${color}`}
  >
    {loading ? (
      <span className='flex items-center justify-center gap-2'>
        <span className={spinnerClass} />
        {loadingText}
      </span>
    ) : (
      label
    )}
  </button>
);

/** Info field (label + value pair) */
const InfoField = ({ label, value, valueClassName = "" }) => (
  <div>
    <p className={labelClass}>{label}</p>
    <p className={`${valueClass} ${valueClassName}`}>{value}</p>
  </div>
);

/** Customer Details Card */
const CustomerDetailsCard = ({ customer, t }) => (
  <section className={cardClass}>
    <h2 className={headingClass}>
      <MdPerson className='text-blue-600' size={20} />
      {t("SHIPPING_MANAGEMENT.CUSTOMER_DETAILS")}
    </h2>
    <div className='grid grid-cols-2 gap-4 text-sm'>
      <InfoField
        label={t("SHIPPING_MANAGEMENT.CUSTOMER_NAME")}
        value={customer?.customerName}
      />
      <InfoField
        label={t("SHIPPING_MANAGEMENT.CUSTOMER_PHONE")}
        value={customer?.phone || "N/A"}
      />
    </div>
  </section>
);

/** Shipping Address Card */
const ShippingAddressCard = ({ address, t }) => (
  <section className={cardClass}>
    <h2 className={headingClass}>
      <MdLocationOn className='text-green-600' size={20} />
      {t("SHIPPING_MANAGEMENT.SHIPPING_ADDRESS")}
    </h2>
    <div className='text-sm'>
      <p className={valueClass}>{address?.address}</p>
      <p className='text-gray-600 dark:text-gray-400'>
        {address?.city}, {address?.state} - {address?.pincode}
      </p>
      <p className='text-gray-600 dark:text-gray-400'>{address?.country}</p>
    </div>
  </section>
);

/** Order Items Card */
const OrderItemsCard = ({ items, t }) => (
  <section className={cardClass}>
    <h2 className={headingClass}>
      <MdInventory className='text-purple-600' size={20} />
      {t("SHIPPING_MANAGEMENT.ORDER_ITEMS", { count: items?.length || 0 })}
    </h2>
    <div className='space-y-3'>
      {items?.map((item, idx) => (
        <div
          key={idx}
          className='flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg'
        >
          <div>
            <p className={valueClass}>{item.productName}</p>
            <p className='text-xs text-gray-500 dark:text-gray-400'>
              {t("SHIPPING_MANAGEMENT.QTY", { count: item.quantity })}
            </p>
          </div>
          <p className='font-semibold dark:text-gray-200'>
            ₹{(item.price * item.quantity).toFixed(2)}
          </p>
        </div>
      ))}
    </div>
  </section>
);

/** Tracking Activities Timeline */
const TrackingTimeline = ({ trackingData, t }) => (
  <div className='mt-4 p-4 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600 rounded-lg'>
    <h3 className='font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2'>
      <MdLocalShipping className='text-green-600' size={18} />
      {t("SHIPPING_MANAGEMENT.SHIPMENT_TRACKING")}
    </h3>

    {/* Current Status */}
    <div className='mb-4 p-3 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg'>
      <p className={labelClass}>{t("SHIPPING_MANAGEMENT.CURRENT_STATUS")}</p>
      <p className='font-semibold text-lg text-green-700 dark:text-green-400 capitalize'>
        {String(
          trackingData.current_status || t("SHIPPING_MANAGEMENT.IN_TRANSIT"),
        ).replace(/_/g, " ")}
      </p>
      {trackingData.etd && (
        <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
          {t("SHIPPING_MANAGEMENT.EXPECTED_DELIVERY", {
            date: trackingData.etd,
          })}
        </p>
      )}
    </div>

    {/* Activities */}
    {trackingData.shipment_track_activities?.length > 0 ? (
      <div className='space-y-3 max-h-60 overflow-y-auto'>
        {trackingData.shipment_track_activities.map((activity, idx) => (
          <div key={idx} className='flex gap-3 items-start'>
            <div className='flex flex-col items-center'>
              <div
                className={`w-3 h-3 rounded-full ${
                  idx === 0 ? "bg-green-500" : "bg-gray-300"
                }`}
              />
              {idx < trackingData.shipment_track_activities.length - 1 && (
                <div className='w-0.5 h-8 bg-gray-200 dark:bg-gray-600' />
              )}
            </div>
            <div className='flex-1 pb-2'>
              <p className='text-sm font-medium text-gray-800 dark:text-gray-200'>
                {activity.activity ||
                  activity["sr-status-label"] ||
                  activity.status ||
                  t("SHIPPING_MANAGEMENT.STATUS_UPDATE")}
              </p>
              <p className='text-xs text-gray-500 dark:text-gray-400'>
                {activity.location || ""}
              </p>
              <p className='text-xs text-gray-400 dark:text-gray-500'>
                {activity.date || ""}
              </p>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <p className='text-sm text-gray-500 dark:text-gray-400 italic'>
        {t("SHIPPING_MANAGEMENT.NO_TRACKING")}
      </p>
    )}
  </div>
);

/** Shipment Success Section (AWB assigned) */
const ShipmentSuccessSection = ({
  awbData,
  pickupData,
  labelUrl,
  trackingData,
  onTrack,
  tracking,
  onGenerateLabel,
  generatingLabel,
  onGenerateManifest,
  generatingManifest,
  onSchedulePickup,
  schedulingPickup,
  t,
}) => (
  <section className='bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 p-5 rounded-xl'>
    <h2 className='font-semibold text-green-800 dark:text-green-300 flex items-center gap-2 mb-3'>
      <MdCheckCircle size={20} />
      {t("SHIPPING_MANAGEMENT.SHIPMENT_CREATED")}
    </h2>

    {/* AWB Info Grid */}
    <div className='grid grid-cols-2 gap-4 text-sm'>
      <InfoField
        label={t("SHIPPING_MANAGEMENT.AWB_NUMBER")}
        value={awbData.awb}
        valueClassName='font-mono font-bold text-lg dark:text-white'
      />
      <InfoField
        label={t("SHIPPING_MANAGEMENT.COURIER")}
        value={awbData.courier}
      />
      {awbData.status && (
        <InfoField
          label={t("SHIPPING_MANAGEMENT.STATUS")}
          value={awbData.status.replace(/_/g, " ")}
          valueClassName='capitalize'
        />
      )}
      {pickupData?.scheduled && (
        <div>
          <p className={labelClass}>{t("SHIPPING_MANAGEMENT.PICKUP")}</p>
          <p className='font-medium text-green-700 dark:text-green-400'>
            {pickupData.date
              ? t("SHIPPING_MANAGEMENT.PICKUP_SCHEDULED_FOR", {
                  date: pickupData.date,
                })
              : t("SHIPPING_MANAGEMENT.PICKUP_SCHEDULED")}
          </p>
        </div>
      )}
    </div>

    {/* Action Buttons */}
    <div className='flex flex-wrap gap-3 mt-4'>
      <ActionButton
        onClick={onTrack}
        loading={tracking}
        loadingText={t("SHIPPING_MANAGEMENT.TRACKING_LOADING")}
        icon={MdLocalShipping}
        label={
          trackingData
            ? t("SHIPPING_MANAGEMENT.REFRESH_TRACKING")
            : t("SHIPPING_MANAGEMENT.TRACKING_BTN")
        }
        color='bg-green-600 hover:bg-green-700'
      />
      <ActionButton
        onClick={onGenerateLabel}
        loading={generatingLabel}
        loadingText={t("SHIPPING_MANAGEMENT.GENERATING")}
        icon={MdPrint}
        label={t("SHIPPING_MANAGEMENT.DOWNLOAD_LABEL")}
        color='bg-blue-600 hover:bg-blue-700'
      />
      {pickupData?.scheduled && (
        <ActionButton
          onClick={onGenerateManifest}
          loading={generatingManifest}
          loadingText={t("SHIPPING_MANAGEMENT.GENERATING")}
          icon={MdDescription}
          label={t("SHIPPING_MANAGEMENT.DOWNLOAD_MANIFEST")}
          color='bg-orange-600 hover:bg-orange-700'
        />
      )}
      {!pickupData?.scheduled && (
        <ActionButton
          onClick={onSchedulePickup}
          loading={schedulingPickup}
          loadingText={t("SHIPPING_MANAGEMENT.SCHEDULING")}
          icon={MdSchedule}
          label={t("SHIPPING_MANAGEMENT.SCHEDULE_PICKUP")}
          color='bg-purple-600 hover:bg-purple-700'
        />
      )}
    </div>

    {/* Label Download Info */}
    {labelUrl && (
      <div className='mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg'>
        <p className='text-sm text-blue-700 dark:text-blue-300'>
          <strong>{t("SHIPPING_MANAGEMENT.LABEL_GENERATED")}</strong>{" "}
          <a
            href={labelUrl}
            target='_blank'
            rel='noreferrer'
            className='underline hover:text-blue-900 dark:hover:text-blue-100'
          >
            {t("SHIPPING_MANAGEMENT.DOWNLOAD_AGAIN")}
          </a>
        </p>
      </div>
    )}

    {/* Pickup Token Info */}
    {pickupData?.scheduled && pickupData.token && (
      <div className='mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg'>
        <p className='text-sm text-purple-700 dark:text-purple-300'>
          <strong>{t("SHIPPING_MANAGEMENT.PICKUP_TOKEN")}</strong>{" "}
          {pickupData.token}
        </p>
      </div>
    )}

    {/* Tracking Timeline */}
    {trackingData && <TrackingTimeline trackingData={trackingData} t={t} />}
  </section>
);

/** Shiprocket Order Banner (Step 1 confirmation) */
const ShiprocketOrderBanner = ({ shiprocketOrder, t }) => (
  <div className={successBannerClass}>
    <p className={successTextClass}>
      {t("SHIPPING_MANAGEMENT.SHIPROCKET_ORDER")}{" "}
      <span className='font-mono'>#{shiprocketOrder?.shiprocket_order_id}</span>
    </p>
    {shiprocketOrder?.shiprocket_shipment_id && (
      <p className='text-xs text-green-600 dark:text-green-400'>
        {t("SHIPPING_MANAGEMENT.SHIPMENT_ID")}{" "}
        {shiprocketOrder.shiprocket_shipment_id}
      </p>
    )}
  </div>
);

/** Courier Selection List */
const CourierSelector = ({ couriers, selectedCourier, onSelect, t }) => (
  <div className='space-y-2 max-h-60 overflow-y-auto'>
    {couriers.map((c) => (
      <div
        key={c.courier_company_id}
        onClick={() => onSelect(c)}
        className={`p-3 border dark:border-gray-600 rounded-lg cursor-pointer transition-all ${
          selectedCourier?.courier_company_id === c.courier_company_id
            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-sm"
            : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
        }`}
      >
        <div className='flex justify-between'>
          <span className={valueClass}>{c.courier_name}</span>
          <span className='font-semibold text-green-600 dark:text-green-400'>
            ₹{c.rate?.toFixed(2)}
          </span>
        </div>
        <p className='text-xs text-gray-500 dark:text-gray-400'>
          {t("SHIPPING_MANAGEMENT.ESTIMATED_DAYS", {
            days: c.estimated_delivery_days,
          })}
        </p>
      </div>
    ))}
  </div>
);

/** Create Shipment Section (shown when no AWB yet) */
const CreateShipmentSection = ({
  shiprocketOrder,
  couriers,
  selectedCourier,
  dimensions,
  onDimensionChange,
  onCreateOrder,
  creatingOrder,
  onCheckServiceability,
  checkingServiceability,
  onSelectCourier,
  onAssignAwb,
  assigningAwb,
  t,
}) => (
  <section className={cardClass}>
    <h2 className={headingClass}>
      <MdLocalShipping className='text-orange-600' size={20} />
      {t("SHIPPING_MANAGEMENT.CREATE_SHIPMENT")}
    </h2>

    {/* Step 1: Dimensions input */}
    {!shiprocketOrder && (
      <>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-4'>
          {["length", "breadth", "height", "weight"].map((field) => (
            <CustomField
              key={field}
              label={`${field.charAt(0).toUpperCase() + field.slice(1)} ${field === "weight" ? t("SHIPPING_MANAGEMENT.WEIGHT_UNIT") : t("SHIPPING_MANAGEMENT.DIMENSION_UNIT")}`}
              type='number'
              name={field}
              value={dimensions[field]}
              handleChange={onDimensionChange}
              step='0.1'
            />
          ))}
        </div>
        <FullWidthButton
          onClick={onCreateOrder}
          loading={creatingOrder}
          loadingText={t("SHIPPING_MANAGEMENT.CREATING_ORDER")}
          label={t("SHIPPING_MANAGEMENT.CREATE_SHIPMENT_ORDER")}
        />
      </>
    )}

    {/* Step 2: Check serviceability */}
    {shiprocketOrder && couriers.length === 0 && (
      <div className='space-y-4'>
        <ShiprocketOrderBanner shiprocketOrder={shiprocketOrder} t={t} />
        <FullWidthButton
          onClick={onCheckServiceability}
          loading={checkingServiceability}
          loadingText={t("SHIPPING_MANAGEMENT.CHECKING")}
          label={t("SHIPPING_MANAGEMENT.CHECK_COURIERS")}
          color='bg-indigo-600 hover:bg-indigo-700'
        />
      </div>
    )}

    {/* Step 3: Courier selection */}
    {couriers.length > 0 && (
      <div className='space-y-4'>
        <ShiprocketOrderBanner shiprocketOrder={shiprocketOrder} t={t} />
        <h3 className='text-sm font-medium text-gray-700 dark:text-gray-300'>
          {t("SHIPPING_MANAGEMENT.SELECT_COURIER")}
        </h3>
        <CourierSelector
          couriers={couriers}
          selectedCourier={selectedCourier}
          onSelect={onSelectCourier}
          t={t}
        />
        {selectedCourier && (
          <FullWidthButton
            onClick={onAssignAwb}
            loading={assigningAwb}
            loadingText={t("SHIPPING_MANAGEMENT.ASSIGNING_AWB")}
            label={t("SHIPPING_MANAGEMENT.BOOK_WITH", {
              courier: selectedCourier.courier_name,
            })}
            color='bg-green-600 hover:bg-green-700'
          />
        )}
      </div>
    )}
  </section>
);

/** Payment Details Card */
const PaymentDetailsCard = ({ payment, t }) => (
  <section className={cardClass}>
    <h2 className={headingClass}>
      <MdPayment className='text-indigo-600' size={20} />
      {t("SHIPPING_MANAGEMENT.PAYMENT_DETAILS")}
    </h2>
    <div className='space-y-3 text-sm'>
      <div className='flex justify-between'>
        <span className={labelClass}>{t("SHIPPING_MANAGEMENT.METHOD")}</span>
        <span className='font-medium uppercase dark:text-gray-200'>
          {payment?.method}
        </span>
      </div>
      <div className='flex justify-between'>
        <span className={labelClass}>
          {t("SHIPPING_MANAGEMENT.PAYMENT_STATUS")}
        </span>
        <span
          className={`font-medium ${
            payment?.status === "paid"
              ? "text-green-600 dark:text-green-400"
              : "text-amber-600 dark:text-amber-400"
          }`}
        >
          {payment?.status}
        </span>
      </div>
    </div>
  </section>
);

/** Price Summary Card */
const PriceSummaryCard = ({ pricing, t }) => {
  const rows = [
    { label: t("SHIPPING_MANAGEMENT.SUBTOTAL"), value: pricing?.subtotal },
    { label: t("SHIPPING_MANAGEMENT.TAX"), value: pricing?.taxAmount },
    { label: t("SHIPPING_MANAGEMENT.SHIPPING"), value: pricing?.shippingCost },
  ];

  return (
    <section className={cardClass}>
      <h2 className={headingClass}>
        <MdReceipt className='text-teal-600' size={20} />
        {t("SHIPPING_MANAGEMENT.PRICE_SUMMARY")}
      </h2>
      <div className='space-y-2 text-sm'>
        {rows.map(({ label, value }) => (
          <div key={label} className='flex justify-between'>
            <span className={labelClass}>{label}</span>
            <span className='dark:text-gray-200'>₹{value?.toFixed(2)}</span>
          </div>
        ))}
        <hr className='my-2 dark:border-gray-600' />
        <div className='flex justify-between font-semibold text-lg'>
          <span className='dark:text-white'>
            {t("SHIPPING_MANAGEMENT.TOTAL")}
          </span>
          <span className='text-green-600 dark:text-green-400'>
            ₹{pricing?.total?.toFixed(2)}
          </span>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════ Main Component ═══════════════════════════ */

const ShippingManagementPage = () => {
  const { t } = useTranslation();
  const { orderId, bussinessUuid } = useParams();
  const navigate = useNavigate();

  // ─── Order State ───
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── Shiprocket State ───
  const [shiprocketOrder, setShiprocketOrder] = useState(null);
  const [couriers, setCouriers] = useState([]);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [awbData, setAwbData] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [labelUrl, setLabelUrl] = useState(null);
  const [pickupData, setPickupData] = useState(null);

  // ─── Package Dimensions ───
  const [dimensions, setDimensions] = useState({
    length: 10,
    breadth: 10,
    height: 10,
    weight: 0.5,
  });

  // ─── Loading Flags ───
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [checkingServiceability, setCheckingServiceability] = useState(false);
  const [assigningAwb, setAssigningAwb] = useState(false);
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [schedulingPickup, setSchedulingPickup] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [generatingManifest, setGeneratingManifest] = useState(false);

  /* ─────────────────── Data Fetching ─────────────────── */

  useEffect(() => {
    if (!bussinessUuid || !orderId) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const orderRes = await getOrderDetails(bussinessUuid, orderId);
        if (!orderRes?.data?.success) {
          toast.error(t("SHIPPING_MANAGEMENT.TOAST_ORDER_LOAD_FAIL"));
          return;
        }
        setOrder(orderRes.data.data);

        // Check for existing Shiprocket order (non-blocking)
        try {
          const statusRes = await getShiprocketStatus(bussinessUuid, orderId);
          if (statusRes?.data?.success && statusRes.data.has_shiprocket) {
            const sr = statusRes.data.shiprocket;
            setShiprocketOrder({
              shiprocket_order_id: sr.orderId,
              shiprocket_shipment_id: sr.shipmentId,
              status: sr.status,
            });
            if (sr.awbCode) {
              setAwbData({
                awb: sr.awbCode,
                courier: sr.courierName,
                tracking_url:
                  sr.trackingUrl ||
                  `https://www.shiprocket.in/shipment-tracking/${sr.awbCode}`,
                status: sr.status,
              });
            }
          }
        } catch {
          // No existing Shiprocket order — that's fine
        }
      } catch (error) {
        console.error("Error fetching order:", error);
        toast.error(t("SHIPPING_MANAGEMENT.TOAST_ORDER_LOAD_ERROR"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [bussinessUuid, orderId]);

  /* ─────────────────── Handlers ─────────────────── */

  const handleDimensionChange = useCallback((e) => {
    const { name, value } = e.target;
    setDimensions((prev) => ({ ...prev, [name]: Number(value) }));
  }, []);

  /** Step 1: Create Shiprocket Order */
  const handleCreateOrder = useCallback(async () => {
    if (!order) return;

    setCreatingOrder(true);
    try {
      const orderItems =
        order.items?.map((item, i) => ({
          name: item.productName || `Product ${i + 1}`,
          sku: item.productId || `SKU${Date.now()}${i}`,
          units: parseInt(item.quantity) || 1,
          selling_price: parseFloat(item.price) || 0,
        })) || [];

      const payload = {
        order_id: order.orderId,
        customer_name: order.customer?.customerName,
        phone: order.customer?.phone,
        email: order.customer?.email || "",
        address: order.shippingAddress?.address,
        address_2: "",
        city: order.shippingAddress?.city,
        state: order.shippingAddress?.state,
        pincode: order.shippingAddress?.pincode,
        payment_method: order.payment?.method === "cod" ? "COD" : "PREPAID",
        length: dimensions.length,
        breadth: dimensions.breadth,
        height: dimensions.height,
        weight: dimensions.weight,
        price: order.pricing?.total || 0,
        shipping_charges: order.pricing?.shippingCost || 0,
        order_items: orderItems,
      };

      const res = await createShiprocketOrder(bussinessUuid, payload);

      if (res?.data?.success) {
        if (res.data.already_exists) {
          toast.info(t("SHIPPING_MANAGEMENT.TOAST_ORDER_EXISTS"));
          if (res.data.awb) {
            setAwbData({
              awb: res.data.awb,
              courier: res.data.courier_name,
              tracking_url: res.data.tracking_url,
              status: res.data.status,
            });
          }
        } else {
          toast.success(t("SHIPPING_MANAGEMENT.TOAST_ORDER_CREATED"));
        }
        setShiprocketOrder({
          shiprocket_order_id: res.data.shiprocket_order_id,
          shiprocket_shipment_id: res.data.shiprocket_shipment_id,
          status: res.data.status,
        });
      } else {
        toast.error(
          res?.data?.message ||
            t("SHIPPING_MANAGEMENT.TOAST_ORDER_CREATE_FAIL"),
        );
      }
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error(
        error.response?.data?.message ||
          t("SHIPPING_MANAGEMENT.TOAST_ORDER_CREATE_SR_FAIL"),
      );
    } finally {
      setCreatingOrder(false);
    }
  }, [order, dimensions, bussinessUuid]);

  /** Step 2: Check Serviceability */
  const handleCheckServiceability = useCallback(async () => {
    if (!shiprocketOrder) return;

    setCheckingServiceability(true);
    try {
      const res = await checkShiprocketServiceability(bussinessUuid, {
        order_id: shiprocketOrder.shiprocket_order_id,
      });

      if (res?.data?.success && res.data.data?.available_courier_companies) {
        const list = res.data.data.available_courier_companies.map((c) => ({
          courier_company_id: c.courier_company_id,
          courier_name: c.courier_name,
          rate: c.rate,
          estimated_delivery_days: c.estimated_delivery_days,
          cod_charges: c.cod_charges || 0,
          freight_charge: c.freight_charge || 0,
        }));
        setCouriers(list);
        toast.success(
          t("SHIPPING_MANAGEMENT.TOAST_COURIERS_FOUND", { count: list.length }),
        );
      } else {
        toast.error(t("SHIPPING_MANAGEMENT.TOAST_NO_COURIERS"));
      }
    } catch (error) {
      console.error("Error checking serviceability:", error);
      toast.error(
        error.response?.data?.message ||
          t("SHIPPING_MANAGEMENT.TOAST_SERVICE_FAIL"),
      );
    } finally {
      setCheckingServiceability(false);
    }
  }, [shiprocketOrder, bussinessUuid]);

  /** Step 3: Assign AWB */
  const handleAssignAwb = useCallback(async () => {
    if (!shiprocketOrder || !selectedCourier) return;

    setAssigningAwb(true);
    try {
      const res = await assignShiprocketAwb(bussinessUuid, {
        shipment_id: shiprocketOrder.shiprocket_shipment_id,
        courier_company_id: selectedCourier.courier_company_id,
        order_id: order.orderId,
      });

      if (res?.data?.success) {
        setAwbData({
          awb: res.data.awb,
          courier: res.data.courier_name || selectedCourier.courier_name,
          tracking_url:
            res.data.tracking_url ||
            `https://www.shiprocket.in/shipment-tracking/${res.data.awb}`,
        });
        toast.success(t("SHIPPING_MANAGEMENT.TOAST_AWB_SUCCESS"));
      } else {
        toast.error(
          res?.data?.message || t("SHIPPING_MANAGEMENT.TOAST_AWB_FAIL"),
        );
      }
    } catch (error) {
      console.error("Error assigning AWB:", error);
      toast.error(
        error.response?.data?.message ||
          t("SHIPPING_MANAGEMENT.TOAST_AWB_FAIL"),
      );
    } finally {
      setAssigningAwb(false);
    }
  }, [shiprocketOrder, selectedCourier, order, bussinessUuid]);

  /** Step 4: Generate Label */
  const handleGenerateLabel = useCallback(async () => {
    if (!shiprocketOrder?.shiprocket_shipment_id) return;

    setGeneratingLabel(true);
    try {
      const res = await generateShiprocketLabel(bussinessUuid, {
        shipment_id: shiprocketOrder.shiprocket_shipment_id,
        order_id: order.orderId,
      });

      if (res?.data?.success) {
        setLabelUrl(res.data.label_url);
        toast.success(t("SHIPPING_MANAGEMENT.TOAST_LABEL_SUCCESS"));
        window.open(res.data.label_url, "_blank");
      } else {
        toast.error(
          res?.data?.message || t("SHIPPING_MANAGEMENT.TOAST_LABEL_FAIL"),
        );
      }
    } catch (error) {
      console.error("Error generating label:", error);
      toast.error(
        error.response?.data?.message ||
          t("SHIPPING_MANAGEMENT.TOAST_LABEL_FAIL"),
      );
    } finally {
      setGeneratingLabel(false);
    }
  }, [shiprocketOrder, order, bussinessUuid]);

  /** Step 5: Schedule Pickup */
  const handleSchedulePickup = useCallback(async () => {
    if (!shiprocketOrder?.shiprocket_shipment_id) return;

    setSchedulingPickup(true);
    try {
      const res = await scheduleShiprocketPickup(bussinessUuid, {
        shipment_id: shiprocketOrder.shiprocket_shipment_id,
        order_id: order.orderId,
      });

      if (res?.data?.success) {
        setPickupData({
          scheduled: true,
          date: res.data.pickup_scheduled_date,
          token: res.data.pickup_token_number,
        });
        toast.success(t("SHIPPING_MANAGEMENT.TOAST_PICKUP_SUCCESS"));
      } else {
        toast.error(
          res?.data?.message || t("SHIPPING_MANAGEMENT.TOAST_PICKUP_FAIL"),
        );
      }
    } catch (error) {
      console.error("Error scheduling pickup:", error);
      toast.error(
        error.response?.data?.message ||
          t("SHIPPING_MANAGEMENT.TOAST_PICKUP_FAIL"),
      );
    } finally {
      setSchedulingPickup(false);
    }
  }, [shiprocketOrder, order, bussinessUuid]);

  /** Step 6: Track Shipment */
  const handleTrackShipment = useCallback(async () => {
    if (!awbData?.awb) return;

    setTracking(true);
    try {
      const res = await trackShiprocketShipment(bussinessUuid, awbData.awb);

      if (res?.data?.success) {
        setTrackingData({
          current_status: res.data.current_status || "In Transit",
          current_status_id: res.data.current_status_id || 0,
          shipment_track: res.data.shipment_track || [],
          shipment_track_activities: res.data.shipment_track_activities || [],
          etd: res.data.etd || null,
          delivered_date: res.data.delivered_date || null,
          track_url: res.data.track_url || null,
        });
        toast.success(t("SHIPPING_MANAGEMENT.TOAST_TRACKING_SUCCESS"));
      } else {
        toast.error(
          res?.data?.message || t("SHIPPING_MANAGEMENT.TOAST_TRACKING_FAIL"),
        );
      }
    } catch (error) {
      console.error("Error tracking shipment:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          t("SHIPPING_MANAGEMENT.TOAST_TRACKING_FAIL"),
      );
    } finally {
      setTracking(false);
    }
  }, [awbData, bussinessUuid]);

  /** Step 7: Generate Manifest */
  const handleGenerateManifest = useCallback(async () => {
    if (!shiprocketOrder?.shiprocket_shipment_id) return;

    setGeneratingManifest(true);
    try {
      const res = await generateShiprocketManifest(bussinessUuid, {
        shipment_id: shiprocketOrder.shiprocket_shipment_id,
        order_id: order.orderId,
      });

      if (res?.data?.success) {
        toast.success(t("SHIPPING_MANAGEMENT.TOAST_MANIFEST_SUCCESS"));
        window.open(res.data.manifest_url, "_blank");
      } else {
        toast.error(
          res?.data?.message || t("SHIPPING_MANAGEMENT.TOAST_MANIFEST_FAIL"),
        );
      }
    } catch (error) {
      console.error("Error generating manifest:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          t("SHIPPING_MANAGEMENT.TOAST_MANIFEST_FAIL"),
      );
    } finally {
      setGeneratingManifest(false);
    }
  }, [shiprocketOrder, order, bussinessUuid]);

  /* ─────────────────── Render: Loading ─────────────────── */

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-white dark:bg-gray-900'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600' />
      </div>
    );
  }

  /* ─────────────────── Render: Not Found ─────────────────── */

  if (!order) {
    return (
      <div className='max-w-5xl mx-auto p-6'>
        <div className='bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-6 text-center'>
          <p className='text-red-600 dark:text-red-400 font-medium'>
            {t("SHIPPING_MANAGEMENT.ORDER_NOT_FOUND")}
          </p>
          <button
            onClick={() => navigate(-1)}
            className='mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700'
          >
            {t("SHIPPING_MANAGEMENT.GO_BACK")}
          </button>
        </div>
      </div>
    );
  }

  /* ─────────────────── Render: Main ─────────────────── */

  return (
    <div className='max-w-6xl mx-auto p-6 space-y-6 dark:bg-gray-900 min-h-screen'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <button
            onClick={() => navigate(-1)}
            className='p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors dark:text-gray-200'
          >
            <MdArrowBack size={24} />
          </button>
          <div>
            <h1 className='text-2xl font-semibold text-gray-800 dark:text-white'>
              {t("SHIPPING_MANAGEMENT.PAGE_TITLE")}
            </h1>
            <p className='text-sm text-gray-500 dark:text-gray-400'>
              {t("SHIPPING_MANAGEMENT.ORDER_ID")} {order.orderId}
            </p>
          </div>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
            order.orderStatus,
          )}`}
        >
          {order.orderStatus}
        </span>
      </div>

      {/* Main Grid */}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Left Column – Order Info & Shipping */}
        <div className='lg:col-span-2 space-y-6'>
          <CustomerDetailsCard customer={order.customer} t={t} />
          <ShippingAddressCard address={order.shippingAddress} t={t} />
          <OrderItemsCard items={order.items} t={t} />

          {/* Shipment Success (AWB assigned) */}
          {awbData && (
            <ShipmentSuccessSection
              awbData={awbData}
              pickupData={pickupData}
              labelUrl={labelUrl}
              trackingData={trackingData}
              onTrack={handleTrackShipment}
              tracking={tracking}
              onGenerateLabel={handleGenerateLabel}
              generatingLabel={generatingLabel}
              onGenerateManifest={handleGenerateManifest}
              generatingManifest={generatingManifest}
              onSchedulePickup={handleSchedulePickup}
              schedulingPickup={schedulingPickup}
              t={t}
            />
          )}

          {/* Create Shipment (no AWB yet) */}
          {!awbData && (
            <CreateShipmentSection
              shiprocketOrder={shiprocketOrder}
              couriers={couriers}
              selectedCourier={selectedCourier}
              dimensions={dimensions}
              onDimensionChange={handleDimensionChange}
              onCreateOrder={handleCreateOrder}
              creatingOrder={creatingOrder}
              onCheckServiceability={handleCheckServiceability}
              checkingServiceability={checkingServiceability}
              onSelectCourier={setSelectedCourier}
              onAssignAwb={handleAssignAwb}
              assigningAwb={assigningAwb}
              t={t}
            />
          )}
        </div>

        {/* Right Column – Payment & Summary */}
        <div className='space-y-6'>
          <PaymentDetailsCard payment={order.payment} t={t} />
          <PriceSummaryCard pricing={order.pricing} t={t} />

          {/* Order Date */}
          <section className='bg-gray-50 dark:bg-gray-800 border dark:border-gray-700 rounded-xl p-4 text-sm text-gray-600 dark:text-gray-400'>
            <p>
              <strong className='dark:text-gray-300'>
                {t("SHIPPING_MANAGEMENT.ORDER_DATE")}
              </strong>{" "}
              {formatDate(order.orderDate)}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ShippingManagementPage;
